import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createReport, deleteReport, getGraphList, getReportDetails, getReportList, getReportTypes, removeSection, updateReport, createSummary, createSection, updateSection, getReportStatus } from '../API/reportApi';
import type { RootState } from '../store/store';
import { mapQueryListAttributes } from '../transform/queryTransform';

export const finalStepId = 'review';

export interface IReport {
    id: string,
    name: string,
    primaryId: string,
    reportType: string,
    searchIds: string[],
    sectionIds: string[],
    sections: IReportSection[],
    searchMap: { [key: string]: any },
    mappedSections?: {
        [id: string]: IReportSection
    }
    displayOrderList: string[]
}

export interface IReportSection {
    id: string,
    name: string,
    content: string,
    rawContent: string,
    description: string,
    sources: string[],
    searchIds: string[],
    searchId: string,
    sectionType: string,
    graphData?: any[],
    extractedFinancialData?: any[],
    flags?: any[]
}

interface ReportState {
    reportList: IReport[],
    reportTypes: string[],
    graphList: any[]
}

export interface IReportCreatePayload {
    searchIds: string[],
    documents: string[],
    primaryId: string,
    reportType: string,
    name: string,
    rootIds: string[],
    reportTargets: string[]
}

export interface IReportSectionCreatePayload {
    reportId: string,
    searchId: string,
    searchIds: string[],
    name: string,
    sectionType: string,
    description: string,
    content: string,
}

export interface IReportCreationResponse {
    reportId: string,
    status: string,
    statusUrl: string
}

export interface IReportStatusResponse {
    status: 'pending' | 'processing' | 'completed' | 'failed',
    progress?: number,
    detail?: string,
    error?: string
}

// Define the initial state using that type
const initialState: ReportState = {
    reportList: [],
    reportTypes: [],
    graphList: []
};

export const callGetReportList = createAsyncThunk(
    'reports/getReportList',
    async (args: { size?: number, from?: number } = {}, { dispatch }) => {
        const { size = 100, from = 0 } = args;
        const response = await getReportList(size, from);
        dispatch(setReportList(response.data.data));
        return response;
    }
);

export const callGetReportTypes = createAsyncThunk(
    'reports/getReportTypes',
    async (args, { dispatch }) => {
      const response = await getReportTypes();
      dispatch(setReportTypes(response.data.data));
    }
)

export const callGetGraphList = createAsyncThunk(
    'reports/getGraphList',
    async (args: { size?: number, from?: number } = {}, { dispatch }) => {
        const { size = 100, from = 0 } = args;
        const response = await getGraphList(size, from);
        dispatch(setGraphList(mapQueryListAttributes(response.data, true)))
        return response.data;
    }
);

export const callGetReportDetails = createAsyncThunk(
    'reports/getReportDetails',
    async (reportId: string) => {
      const response = await getReportDetails(reportId);
      return response.data;
    }
)

export const callGetReportStatus = createAsyncThunk(
    'reports/getReportStatus',
    async (reportId: string) => {
      const response = await getReportStatus(reportId);
      return response.data;
    }
)

export const callCreateReport = createAsyncThunk(
    'reports/createReport',
    async (payload: IReportCreatePayload, { dispatch }) => {
        const response = await createReport(payload);
        return response;
    }
)

export const callCreateSection = createAsyncThunk(
    'reports/createSection',
    async (payload: IReportSectionCreatePayload, { dispatch }) => {
        const response = await createSection(payload);
        return response.data;
    }
)

export const callDeleteReport = createAsyncThunk(
    'reports/deleteReport',
    async (reportId: string, { dispatch }) => {
        const response = await deleteReport(reportId);
        dispatch(callGetReportList({}));
        return response.data;
    }
)


export const callCreateSummary = createAsyncThunk(
    'reports/createSummary',
    async (searchId: string, { dispatch }) => {
      const response = await createSummary(searchId);
      return response.data;
    }
)

export const callRemoveSection = createAsyncThunk<any, {reportId: string, sectionId: string}>(
    'reports/removeSection',
    async ({ reportId, sectionId }, { dispatch }) => {
        const response = await removeSection(reportId, sectionId);
        return response.data;
    }
);

export const callUpdateReport = createAsyncThunk<any, IReport>(
    'reports/updateReport',
    async (report, { dispatch }) => {
        
        const updatedReport = {
            ...report,
            sections: report.sections.map(section => {
                if (section.sectionType === 'graph') {
                    const { graphData, ...sectionWithoutGraphData } = section;
                    return {
                        ...sectionWithoutGraphData,
                        rawContent: "",
                        content: "",
                    };
                } else if (section.sectionType === 'flags') {
                    const { flags, ...sectionWithoutFlags } = section;
                    return {
                        ...sectionWithoutFlags,
                    };
                }
                return section;
            }),
            mappedSections: report.mappedSections
                ? Object.fromEntries(
                    Object.entries(report.mappedSections).map(([id, section]) => {
                        if (section.sectionType === 'graph') {
                            const { graphData, ...sectionWithoutGraphData } = section;
                            return [
                                id,
                                {
                                    ...sectionWithoutGraphData,
                                    rawContent: "",
                                    content: "",
                                }
                            ];
                        } else if (section.sectionType === 'flags') {
                            const { flags, ...sectionWithoutFlags } = section;
                            return [
                                id,
                                {
                                    ...sectionWithoutFlags,
                                }
                            ];
                        }
                        return [id, section];
                    })
                )
                : undefined
        };
        const response = await updateReport(updatedReport);
        return response.data;
    }
)

export const callUpdateSection = createAsyncThunk<any, {section: IReportSection, generate: boolean}>(
    'reports/updateSection',
    async ({ section, generate }, { dispatch }) => {
        const { flags, graphData, ...sectionForApi } = section;

        const response = await updateSection(sectionForApi, generate);
        return response.data;
    }
)

export const reportSlice = createSlice({
  name: 'report',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setReportList: (state, action: PayloadAction<IReport[]>) => {
      state.reportList = action.payload;
    },
    setReportTypes: (state, action: PayloadAction<string[]>) => {
      state.reportTypes = action.payload
    },
    setGraphList: (state, action: PayloadAction<any[]>) => {
        state.graphList = action.payload;
    }
  }
});

export const {
  setReportList,
  setReportTypes,
  setGraphList
} = reportSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectReportList = (state: RootState): IReport[] => state.report.reportList;
export const selectReportTypes = (state: RootState): string[] => state.report.reportTypes;
export const selectGraphList = (state: RootState): any[] => state.report.graphList;

export default reportSlice.reducer;
